import { UploadPageLayout } from "components/modules/Layout/UploadPageLayout/UploadPageLayout";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import type { GetStaticProps } from "next";
import { Navbar } from "components/modules/Navigation/Navbar/Navbar";
import Head from "next/head";
import { LanguageProvider } from "client/contexts/LanguageProvider";
import { commonLanguageFiles } from "client/config";
import { translateFunc } from "client/utils/translateFunc";
import { Translate } from "components/common/Translate/Translate";
import dynamic from "next/dynamic";
import { JpegPngCompressorTutorial } from "components/modules/Tutorial/CompressorTutorial/JpegPngCompressorTutorial";

import React from "react";
import { GifCompressorFeaturesContent } from "components/features/GifCompressor/GifCompressorFeaturesContent";
import { RouteNames } from "@/RouteNames";
import GifCompressorPreToolsArea from "components/features/GifCompressor/GifCompressorPreToolsArea";

const CompressUploader = dynamic<{}>(() =>
    import("components/modules/Uploader/Compress/CompressUploader").then((mod) => mod.CompressUploader),
);

const Page = () => {
    const title = translateFunc("page-title");
    const description = translateFunc("meta-description");

    return (
        <>
            <Head>
                <title>{title}</title>
                <meta name="description" content={description} />
                <meta property="og:type" content="website" />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:image" content="/social.png" />
                <meta property="twitter:card" content="summary" />
                <meta property="twitter:title" content={title} />
                <meta property="twitter:description" content={description} />
            </Head>
            <UploadPageLayout
                navbar={<Navbar url={RouteNames.gifCompressor} />}
                title={<Translate keyName="title" />}
                subTitle={<Translate keyName="sub-title" />}
                uploader={<CompressUploader />}
                content={<GifCompressorFeaturesContent />}
                tutorial={<JpegPngCompressorTutorial />}
                preToolsSlot={<GifCompressorPreToolsArea />}
                pageUrl={RouteNames.gifCompressor}
            />
        </>
    );
};

const files = ["gif-compressor-landing", "uploader", ...commonLanguageFiles];

const LanguageWrapper = () => {
    return (
        <LanguageProvider files={files}>
            <Page />
        </LanguageProvider>
    );
};

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
    props: {
        ...(await serverSideTranslations(locale ?? "", files)),
    },
});

export default LanguageWrapper;
